/**
 * USP Team Wade Tier
 * Calls POST /apps/coupon-api/loyalty with action=get_customer to get the real
 * Growave VIP tier, then updates the Team Wade USP icon and text.
 *
 * Required data attributes on [data-usp-team-wade]:
 *   data-customer-id  — Shopify customer ID (only present when logged in)
 *
 * Hidden <template data-tier-icon="..."> elements provide pre-rendered SVG icons.
 *
 * Tiers: Rising Star → All Star → Super Star → Hall of Fame
 * Guests / non-members see the default "Join Team Wade" unchanged.
 */
(function () {
  'use strict';

  var CONTAINER_SELECTOR = '[data-usp-team-wade]';
  var CACHE_KEY = 'wade_loyalty_tier';
  var CACHE_EXPIRY_KEY = 'wade_loyalty_tier_expiry';
  var CACHE_TTL_MS = 10 * 60 * 1000; // 10 min cache

  // Tier config: normalized name → display data
  var TIERS = {
    'rising star': {
      templateId: 'rising_star',
      title_en: 'Welcome! Rising Star Member.',
      title_ja: 'ようこそ！Rising Star メンバー。',
      desc_en: 'Keep earning to unlock bigger rewards.',
      desc_ja: 'さらにポイントを貯めて特典をアンロック。'
    },
    'all star': {
      templateId: 'all_star',
      title_en: 'Welcome! All Star Member.',
      title_ja: 'ようこそ！All Star メンバー。',
      desc_en: 'Enjoy exclusive All Star perks.',
      desc_ja: 'All Star 限定特典をお楽しみください。'
    },
    'super star': {
      templateId: 'super_star',
      title_en: 'Welcome! Super Star Member.',
      title_ja: 'ようこそ！Super Star メンバー。',
      desc_en: 'Premium rewards unlocked.',
      desc_ja: 'プレミアム特典がアンロックされました。'
    },
    'hall of fame': {
      templateId: 'hall_of_fame',
      title_en: 'Welcome! Hall of Fame Member.',
      title_ja: 'ようこそ！Hall of Fame メンバー。',
      desc_en: 'You\'ve reached the highest tier!',
      desc_ja: '最高ランクに到達しました！'
    }
  };

  // ---- Cache helpers ----

  function getCachedTier() {
    try {
      var expiry = localStorage.getItem(CACHE_EXPIRY_KEY);
      if (expiry && Date.now() < parseInt(expiry, 10)) {
        var cached = localStorage.getItem(CACHE_KEY);
        if (cached) return JSON.parse(cached);
      }
    } catch (e) { /* silent */ }
    return null;
  }

  function setCachedTier(data) {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(data));
      localStorage.setItem(CACHE_EXPIRY_KEY, String(Date.now() + CACHE_TTL_MS));
    } catch (e) { /* silent */ }
  }

  function clearCache() {
    try {
      localStorage.removeItem(CACHE_KEY);
      localStorage.removeItem(CACHE_EXPIRY_KEY);
    } catch (e) { /* silent */ }
  }

  // ---- API call ----

  /**
   * Call POST /apps/loyalty with action=get_customer
   * Returns promise-like via callback (ES5 compatible)
   */
  function fetchLoyaltyData(customerId, callback) {
    fetch('/apps/coupon-api/loyalty', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'get_customer',
        customerIdentifier: customerId
      })
    })
    .then(function (res) {
      if (!res.ok) throw new Error('Loyalty API responded ' + res.status);
      return res.json();
    })
    .then(function (data) {
      callback(null, data);
    })
    .catch(function (err) {
      /* API error - fail silently, keep default USP */
      callback(err, null);
    });
  }

  /**
   * Extract tier name from the loyalty API response.
   *
   * Actual response structure:
   * { success: true, data: { currentTier: { id, title, image, achievedAt } } }
   */
  function extractTierName(resp) {
    if (!resp) return '';

    // Unwrap { success, data } wrapper
    var data = resp.data || resp;

    // Primary path: data.currentTier.title (confirmed from API)
    if (data.currentTier && data.currentTier.title) {
      return data.currentTier.title;
    }

    // Fallback paths
    if (data.tier_name) return data.tier_name;
    if (data.tierName) return data.tierName;
    if (data.vip_tier_name) return data.vip_tier_name;

    if (data.tier) {
      if (typeof data.tier === 'string') return data.tier;
      if (data.tier.name) return data.tier.name;
      if (data.tier.title) return data.tier.title;
    }

    if (data.vipTier) {
      if (typeof data.vipTier === 'string') return data.vipTier;
      if (data.vipTier.name) return data.vipTier.name;
    }

    return '';
  }

  /**
   * Match extracted name to our tier config
   */
  function matchTier(tierName) {
    if (!tierName) return null;
    var normalized = tierName.toLowerCase().trim();
    for (var slug in TIERS) {
      if (TIERS.hasOwnProperty(slug)) {
        if (normalized.indexOf(slug) !== -1) return TIERS[slug];
      }
    }
    return null;
  }

  // ---- DOM update ----

  function applyTier(tierData) {
    var containers = document.querySelectorAll(CONTAINER_SELECTOR);
    if (!containers.length || !tierData) return;

    var lang = (document.documentElement.lang || 'en').toLowerCase().split('-')[0];
    var isJa = lang === 'ja';

    for (var i = 0; i < containers.length; i++) {
      var container = containers[i];

      // Replace the default team_wade SVG with the tier-specific icon from hidden <template>
      var iconWrap = container.querySelector('.t4s-usp-icon');
      if (iconWrap && tierData.templateId) {
        var tmpl = document.querySelector('template[data-tier-icon="' + tierData.templateId + '"]');
        if (tmpl && tmpl.content) {
          iconWrap.innerHTML = '';
          iconWrap.appendChild(tmpl.content.cloneNode(true));
        }
      }

      // Update title and link
      var titleEl = container.querySelector('.t4s-usp-title');
      if (titleEl) {
        titleEl.textContent = isJa ? tierData.title_ja : tierData.title_en;
        titleEl.href = '/pages/reward';
      }

      // Update description
      var descEl = container.querySelector('.t4s-usp-desc');
      if (descEl) {
        descEl.textContent = isJa ? tierData.desc_ja : tierData.desc_en;
      }
    }
  }

  // ---- Main ----

  function init() {
    var container = document.querySelector(CONTAINER_SELECTOR);
    if (!container) return;

    var customerId = container.getAttribute('data-customer-id');

    // Guest user: no customer ID, keep default "Join Team Wade"
    if (!customerId) {
      /* Guest user - keep default 'Join Team Wade' */
      clearCache(); // clear stale cache from previous session
      return;
    }

    // Check cache first
    var cached = getCachedTier();
    if (cached && cached.customerId === customerId) {
      /* Using cached tier data */
      var tierData = matchTier(cached.tierName);
      if (tierData) applyTier(tierData);
      return;
    }

    // Fetch from API
    fetchLoyaltyData(customerId, function (err, data) {
      if (err || !data) return;

      var tierName = extractTierName(data);
      /* Tier detected from API */

      if (tierName) {
        // Cache the result
        setCachedTier({ customerId: customerId, tierName: tierName });

        var tierData = matchTier(tierName);
        if (tierData) applyTier(tierData);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
